import React, {
  useState,
  useRef,
  useCallback,
} from "react";
import {
  EnvelopeIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { Helmet } from "react-helmet-async";

const Contact: React.FC = () => {
  const [subject, setSubject] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastColor, setToastColor] =
    useState("bg-gray-500");
  const [progress, setProgress] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toastTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressInterval = useRef<NodeJS.Timeout | null>(
    null
  );
  const subjectOptions = [
    { value: "cooperation", label: "Cooperation" },
    { value: "interest", label: "Interest" },
  ];

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setSubject("");
    setMessage("");
  };

  const handleCloseToast = () => {
    setShowToast(false);
    setProgress(100);
    if (toastTimeout.current)
      clearTimeout(toastTimeout.current);
    if (progressInterval.current)
      clearInterval(progressInterval.current);
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      console.log("Submit triggered:", {
        isSubmitting,
        firstName,
        lastName,
        email,
        phone,
        subject,
      });
      if (isSubmitting) return;
      if (
        !firstName ||
        !lastName ||
        !email ||
        !phone ||
        !subject
      ) {
        setToastMsg("Please fill in all required fields.");
        setToastColor("bg-red-500");
        setShowToast(true);
        return;
      }
      setIsSubmitting(true);
      setToastMsg("Sending...");
      setToastColor("bg-gray-500");
      setShowToast(true);

      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            phone,
            subject,
            message: message || "",
          }),
        });
        const data = await response.json();
        console.log("Response:", data);
        if (response.ok) {
          setToastMsg(
            data.message || "Contact sent successfully!"
          );
          setToastColor("bg-green-500");
          resetForm();
        } else {
          setToastMsg(
            data.error || "Error sending contact."
          );
          setToastColor("bg-red-500");
        }
      } catch (error) {
        console.log("Fetch error:", error);
        setToastMsg("Error connecting to server.");
        setToastColor("bg-red-500");
      } finally {
        setIsSubmitting(false);
      }

      setProgress(100);
      let elapsed = 0;
      const duration = 4000;
      const interval = 40;
      if (progressInterval.current)
        clearInterval(progressInterval.current);
      progressInterval.current = setInterval(() => {
        elapsed += interval;
        setProgress(
          Math.max(0, 100 - (elapsed / duration) * 100)
        );
      }, interval);
      if (toastTimeout.current)
        clearTimeout(toastTimeout.current);
      toastTimeout.current = setTimeout(() => {
        setShowToast(false);
        setProgress(100);
        if (progressInterval.current)
          clearInterval(progressInterval.current);
      }, duration);
    },
    [firstName, lastName, email, phone, subject, message]
  );

  return (
    <>
      <Helmet>
        <title>Contact Us - VEA Group</title>
        <meta
          name="description"
          content="Contact VEA Group for partnership, business, or career opportunities. Reach out to our Vietnam and US headquarters or email us directly."
        />
        <meta
          name="keywords"
          content="contact, VEA Group, partnership, business, career, Vietnam, US, email"
        />
        <link
          rel="canonical"
          href="https://veagroup.global/contact"
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content="Contact Us - VEA Group"
        />
        <meta
          property="og:description"
          content="Contact VEA Group for partnership, business, or career opportunities. Reach out to our Vietnam and US headquarters or email us directly."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://veagroup.global/contact"
        />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content="Contact Us - VEA Group"
        />
        <meta
          name="twitter:description"
          content="Contact VEA Group for partnership, business, or career opportunities. Reach out to our Vietnam and US headquarters or email us directly."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>
      <header
        className="header relative w-full bg-cover bg-center text-center"
        style={{
          backgroundImage:
            "url('/images/contact_background.jpg')",
          minHeight: "400px",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <h1 className="text-5xl font-bold text-white">
            Contact Us
          </h1>
        </div>
      </header>
      <div className="min-h-screen bg-[#f7f7f7] dark:bg-[#18181b] py-10 px-2 md:px-0 font-primary">
        <div className="w-full max-w-5xl mx-auto bg-white dark:bg-[#23232a] rounded-2xl shadow-lg flex flex-col md:flex-row overflow-hidden">
          <div className="bg-black dark:bg-white text-white flex flex-col justify-between p-8 md:w-1/2 min-h-[500px]">
            <div>
              <h2 className="text-2xl font-bold mb-6 dark:text-black">
                Contact Information
              </h2>
              <p className="mb-5 text-gray-300 dark:text-gray-600">
                Headquarters in Viet Nam:
              </p>
              <div className="flex items-start mb-6">
                <MapPinIcon className="w-6 h-6 text-[#FE6019] mr-4 mt-1" />
                <span className="text-base dark:text-black">
                  115 Street No. 11, Thu Duc Ward, Ho Chi
                  Minh City
                </span>
              </div>
              <div className="flex items-start mb-6">
                <MapPinIcon className="w-9 h-9 text-[#FE6019] mr-4 mt-1" />
                <span className="text-base dark:text-black">
                  HSC Building, 162B Dien Bien Phu, Xuan Hoa
                  Ward, Ho Chi Minh City
                </span>
              </div>
              <p className="mb-5 text-gray-300 dark:text-gray-600">
                Headquarters in the United States:
              </p>
              <div className="flex items-start mb-6">
                <MapPinIcon className="w-6 h-6 text-[#FE6019] mr-4 mt-1" />
                <span className="text-base dark:text-black">
                  5900 Balcones Drive STE 100, Austin, TX
                  78731
                </span>
              </div>
              <div className="flex items-center mb-6">
                <EnvelopeIcon className="w-6 h-6 text-[#FE6019] mr-4" />
                <span className="text-base dark:text-black">
                  hr@veagroup.global
                </span>
              </div>
              <div className="flex items-center mb-6">
                <EnvelopeIcon className="w-6 h-6 text-[#FE6019] mr-4" />
                <span className="text-base dark:text-black">
                  phongnguyen@veagroup.global
                </span>
              </div>
            </div>
            <div className="absolute left-0 bottom-0 opacity-20 pointer-events-none select-none">
              <svg width="200" height="100">
                <circle
                  cx="100"
                  cy="100"
                  r="100"
                  fill="#fff"
                />
              </svg>
            </div>
          </div>
          <form
            className="flex-1 bg-white dark:bg-[#23232a] p-8 flex flex-col justify-center"
            onSubmit={handleSubmit}
          >
            <fieldset className="border-0 p-0 mb-4">
              <legend className="sr-only">
                Personal Information
              </legend>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-gray-500 dark:text-gray-300 text-sm mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) =>
                      setFirstName(e.target.value)
                    }
                    className="w-full border-b border-gray-300 dark:border-gray-600 focus:border-[#FE6019] outline-none py-2 bg-transparent text-gray-900 dark:text-white rounded-lg"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-500 dark:text-gray-300 text-sm mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) =>
                      setLastName(e.target.value)
                    }
                    className="w-full border-b border-gray-300 dark:border-gray-600 focus:border-[#FE6019] outline-none py-2 bg-transparent text-gray-900 dark:text-white rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-gray-500 dark:text-gray-300 text-sm mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    className="w-full border-b border-gray-300 dark:border-gray-600 focus:border-[#FE6019] outline-none py-2 bg-transparent text-gray-900 dark:text-white rounded-lg"
                    required
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-500 dark:text-gray-300 text-sm mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) =>
                      setPhone(e.target.value)
                    }
                    className="w-full border-b border-gray-300 dark:border-gray-600 focus:border-[#FE6019] outline-none py-2 bg-transparent text-gray-900 dark:text-white rounded-lg"
                    required
                  />
                </div>
              </div>
            </fieldset>
            <fieldset className="border-0 p-0 mb-4">
              <legend className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">
                What is your concerned about?
              </legend>
              <div className="flex flex-wrap gap-4">
                {subjectOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="subject"
                      value={opt.value}
                      checked={subject === opt.value}
                      onChange={(e) =>
                        setSubject(e.target.value)
                      }
                      className="form-radio accent-[#FE6019] w-4 h-4 mr-2"
                    />
                    <span className="text-gray-700 dark:text-gray-200 text-sm">
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="border-0 p-0 mb-6">
              <legend className="sr-only">Message</legend>
              <label className="block text-gray-500 dark:text-gray-300 text-sm mb-1">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border-b border-gray-300 dark:border-gray-600 focus:border-[#FE6019] outline-none py-2 bg-transparent text-gray-900 dark:text-white resize-none rounded-lg"
                rows={2}
                placeholder="Write your message.."
              />
            </fieldset>
            <div className="flex justify-end">
              <button
                type="submit"
                className="w-full md:w-auto px-8 py-3 rounded-lg bg-black dark:bg-white text-white dark:text-black font-semibold shadow hover:bg-[#FE6019] hover:text-white dark:hover:bg-[#FE6019] dark:hover:text-white transition-colors duration-200"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Sending..."
                  : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
      {showToast && (
        <div
          className={`fixed top-6 right-6 z-50 min-w-[260px] max-w-xs ${toastColor} text-white font-primary font-semibold shadow-lg rounded-lg overflow-hidden animate-fade-in-out`}
        >
          <div className="flex items-center justify-between px-4 py-3">
            <span>{toastMsg}</span>
            <button
              onClick={handleCloseToast}
              className="ml-4 text-white hover:text-gray-200 text-lg font-bold focus:outline-none"
            >
              ×
            </button>
          </div>
          <div className="h-1 bg-opacity-30 bg-white">
            <div
              style={{ width: `${progress}%` }}
              className="h-1 bg-white transition-all duration-100"
            ></div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fade-in-out {
          0% { opacity: 0; transform: translateY(-20px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-fade-in-out {
          animation: fade-in-out 4s;
        }
      `}</style>
    </>
  );
};

export default Contact;
